import React, { useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemText, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { FASTAPI_HOST } from '../settings/config';

const authConfig = () => ({ headers: { Authorization: `Bearer ${Cookies.get('user_token') || ''}` } });

export default function ConversationStorageControls({ sessionId, storageMode = 'temporary', definitions = [], onRestore, onStorageModeChange, currentLang = 'English' }) {
  const localized = currentLang === 'Simplified Chinese' ? { save: '保存草稿', saved: '已保存', saveHelp: '私密保存这个草稿 30 天', history: '已保存对话', empty: '暂无已保存的对话', continue: '继续', close: '关闭', expired: '这个临时对话已失效。请重新开始一条对话。', saveError: '这个对话无法保存。' }
    : currentLang === 'Traditional Chinese' ? { save: '儲存草稿', saved: '已儲存', saveHelp: '私密儲存這個草稿 30 天', history: '已儲存對話', empty: '尚無已儲存的對話', continue: '繼續', close: '關閉', expired: '這個臨時對話已失效。請重新開始一條對話。', saveError: '這個對話無法儲存。' }
      : currentLang === 'French' ? { save: 'Enregistrer', saved: 'Enregistré', saveHelp: 'Enregistrer ce brouillon privé pendant 30 jours', history: 'Conversations enregistrées', empty: 'Aucune conversation enregistrée.', continue: 'Continuer', close: 'Fermer', expired: 'Cette conversation temporaire a expiré. Commencez une nouvelle conversation.', saveError: 'Cette conversation n’a pas pu être enregistrée.' }
        : { save: 'Save draft', saved: 'Saved', saveHelp: 'Save this private draft for 30 days', history: 'Saved conversations', empty: 'No saved conversations yet.', continue: 'Continue', close: 'Close', expired: 'This temporary conversation has expired. Please start a new conversation.', saveError: 'This conversation could not be saved.' };
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadHistory = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.get(`${FASTAPI_HOST}/api/conversations`, authConfig());
      const allowed = new Set(definitions);
      setItems((data?.conversations || []).filter((item) => !allowed.size || allowed.has(item.definition)));
      setOpen(true);
    } catch (requestError) { setError(requestError?.response?.data?.detail || 'Saved conversations could not be loaded.'); setOpen(true); }
    finally { setBusy(false); }
  };
  const saveDraft = async () => {
    if (!sessionId || storageMode !== 'temporary') return;
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${FASTAPI_HOST}/api/conversations/${sessionId}/storage`, { storage_mode: 'draft' }, authConfig());
      onStorageModeChange?.(data?.context?.storage_mode || 'draft');
    } catch (requestError) {
      setError(requestError?.response?.status === 404
        ? localized.expired
        : requestError?.response?.data?.detail || localized.saveError);
    }
    finally { setBusy(false); }
  };
  const restore = async (id) => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.get(`${FASTAPI_HOST}/api/conversations/${id}`, authConfig());
      onRestore?.(data); onStorageModeChange?.(data?.context?.storage_mode || 'draft'); setOpen(false);
    } catch (requestError) { setError(requestError?.response?.data?.detail || 'This conversation could not be restored.'); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true); setError('');
    try { await axios.delete(`${FASTAPI_HOST}/api/conversations/${id}`, authConfig()); setItems((current) => current.filter((item) => item.session_id !== id)); }
    catch (requestError) { setError(requestError?.response?.data?.detail || 'This conversation could not be deleted.'); }
    finally { setBusy(false); }
  };

  return <>
    <Stack direction="row" spacing={0.75} alignItems="center">
      {sessionId && (
        <Tooltip title={storageMode === 'temporary' ? localized.saveHelp : localized.saved}>
          <span>
            <IconButton
              size="small"
              aria-label={storageMode === 'temporary' ? localized.save : localized.saved}
              disabled={busy || storageMode !== 'temporary'}
              onClick={saveDraft}
              sx={{ border: '1px solid #d1d5db', color: storageMode === 'temporary' ? '#3A6752' : '#2f7d32' }}
            >
              {storageMode === 'temporary' ? <BookmarkAddOutlinedIcon /> : <CloudDoneOutlinedIcon />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title={localized.history}>
        <span>
          <IconButton
            size="small"
            aria-label={localized.history}
            disabled={busy}
            onClick={loadHistory}
            sx={{ border: '1px solid #d1d5db', color: '#3A6752' }}
          >
            <HistoryRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>{localized.history}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!items.length && !busy ? <Typography color="text.secondary">{localized.empty}</Typography> : <List disablePadding>{items.map((item) => <ListItem key={item.session_id} divider secondaryAction={<IconButton aria-label="Delete saved conversation" onClick={() => remove(item.session_id)}><DeleteOutlineRoundedIcon /></IconButton>}><ListItemText primary={item.title || 'Conversation'} secondary={`${item.status} · ${new Date(item.updated_at).toLocaleString()}`} /><Button size="small" onClick={() => restore(item.session_id)} sx={{ mr: 5 }}>{localized.continue}</Button></ListItem>)}</List>}
      </DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)}>{localized.close}</Button></DialogActions>
    </Dialog>
    <Snackbar open={Boolean(error) && !open} autoHideDuration={8000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert severity="error" variant="filled" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
    </Snackbar>
  </>;
}
